import React, { useCallback, useEffect } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import { useForm, TextField } from '@travelity/form';
import { Box, Stack } from '@mui/material';
import { UseBookingsLazyData, useExportBookings } from '@travelity/api';
import PerfectScrollbar from 'react-perfect-scrollbar';
import { Button, LoadingOverlay } from '@travelity/ui';

const formId = 'export-modal';

export interface ExportBookingsModalProps {
    handleCancel: () => void;
    open: boolean;
    filters: UseBookingsLazyData;
}

const ExportBookingsModal: React.FC<ExportBookingsModalProps> = ({
    handleCancel,
    open,
    filters,
}) => {
    const downloadPdf = (url: string, filename: string) => {
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const { mutate: generate, isLoading } = useExportBookings(filters, {
        onSuccess: data => {
            // window.open(data.url, '_blank', 'noopener,noreferrer');
            if (data.url) {
                downloadPdf(data.url, data.name);
            }
            handleCancel();
        },
    });

    const { Form, trigger, reset, watch } = useForm<{ name: string }>({
        mode: 'onChange',
        reValidateMode: 'onChange',
        onSubmit: useCallback(
            (data: { name: string }) => {
                generate(data);
            },
            [generate]
        ),
        defaultValues: {
            name: '',
        },
    });

    useEffect(() => {
        reset({ name: '' });
        if (open) trigger();
    }, [open, reset]);

    return (
        <Dialog
            open={open}
            onClose={handleCancel}
            PaperProps={{ sx: { py: 1, bgcolor: '#fff' } }}
        >
            <DialogContent
                sx={{
                    overflow: 'hidden',
                    pr: 1,
                    '& > div': {
                        pr: 2,
                        maxHeight: 'calc(100vh - 212px)',
                    },
                }}
            >
                <PerfectScrollbar>
                    <Form id={formId}>
                        <Box
                            sx={{
                                color: '#C0C4CE',
                                fontSize: '21px',
                                fontWeight: 600,
                            }}
                        >
                            Enter file details for export
                        </Box>

                        <Stack
                            direction="column"
                            gap={2}
                            sx={{ pt: 2, width: '388px' }}
                        >
                            <TextField
                                name="name"
                                label="File name"
                                placeholder="Type File name"
                            />
                        </Stack>
                    </Form>
                </PerfectScrollbar>
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2 }}>
                <Button
                    color="neutral"
                    variant="contained"
                    onClick={handleCancel}
                    sx={{
                        px: '26px',
                        py: '10px',
                    }}
                >
                    Cancel
                </Button>

                <Button
                    color="secondary"
                    variant="contained"
                    type="submit"
                    form={formId}
                    disabled={!watch('name')}
                    sx={{
                        py: '10px',
                    }}
                >
                    Export
                </Button>
            </DialogActions>
            {isLoading && <LoadingOverlay title="Generating file" />}
        </Dialog>
    );
};

export default ExportBookingsModal;
